var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { PatientConsentViewModel } from "./PatientConsentViewModel";
import { computed, observable, when } from "mobx";
import { SmartAuthState, SmartAuthType } from "../Logic/SmartAuthManager";
import { NOT_LOADED } from "../Infra/RemoteData";
import { getObservableT } from "../Infra/i18next";
import { dtcOrganizationId } from "healthex-common/organization.dtos";
import { NamedRoutes } from "./NamedRoutes";
export const LOGGED_IN_TO_CLEAR_TRANSITION_DURATION = 1.5;
const t = getObservableT("patients");
export class PatientConsentLoginViewModel extends PatientConsentViewModel {
    constructor(container) {
        super(container);
        this.previewModeTimer = null;
        this.healthExVerificationProgress = 0;
        this.loginUIState = "howItWorks";
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.smartAuthManager = container.smartAuthManager;
        this.loginUIManager = container.loginUIManager;
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
    }
    run() {
        this.disposer.reaction(() => this.loginUIState, (loginUIState) => {
            if (loginUIState === "verifyingWithHealthEx") {
                const animationTimer = setTimeout(() => {
                    this.healthExVerificationProgress = 100;
                }, 50);
                const navigationTimer = setTimeout(() => {
                    this.goToNextPage();
                }, LOGGED_IN_TO_CLEAR_TRANSITION_DURATION * 1000);
                return () => {
                    clearTimeout(animationTimer);
                    clearTimeout(navigationTimer);
                };
            }
            return undefined;
        }, { fireImmediately: false, name: "PatientConsentLoginViewModel_loginUIState" });
    }
    stop() {
        this.disposer.dispose();
        this.clearPreviewModeTimer();
    }
    get howItWorksFindRecordsText() {
        return this.isDataUpdate ? t("Find your most recent records") : t("Confirm access and find records");
    }
    get howItWorksTitle() {
        return this.isPatientLoggedIn ? t("Verify your identity") : t("Verify your identity with CLEAR");
    }
    get howItWorksSupportingText() {
        return this.isPatientLoggedIn
            ? t("You’re already verified with CLEAR. We just need a quick check to continue.")
            : t("For your security, HealthEx uses CLEAR to confirm it’s really you before accessing your health records.");
    }
    get howItWorksReviewRecordsText() {
        return this.isIAS && !this.isPatientLoggedIn ? t("Review and access your records") : t("Review and share your records");
    }
    verifyWithClearIfNeeded() {
        if (!this.isPatientLoggedIn) {
            void this.login(this.loginUIState === "verifyingWithClearError");
        }
        else {
            this.loginUIState = "verifyingWithHealthEx";
        }
    }
    get isLoggingIn() {
        return this.smartAuthManager.state === SmartAuthState.IN_PROGRESS;
    }
    async login(isTryingAgain) {
        var _a;
        if (this.isScreenshotMode)
            return;
        this.patientConsentUIManager.setHasLoggedInDuringConsent();
        if (this.isPreview) {
            this.clearPreviewModeTimer();
            this.previewModeTimer = setTimeout(() => {
                this.goToNextPage();
            }, 1200);
            return;
        }
        const smartAuthType = SmartAuthType.PATIENT_AUTH;
        let studyOrgId;
        if (this.studyInfo !== NOT_LOADED && this.studyInfo !== null) {
            studyOrgId = (_a = this.studyInfo.addedByOrganization) === null || _a === void 0 ? void 0 : _a.id;
        }
        const determineOrgId = async () => {
            await when(() => this.loginUIManager.organization !== NOT_LOADED);
            if (studyOrgId) {
                return studyOrgId;
            }
            if (this.loginUIManager.organization !== NOT_LOADED && this.loginUIManager.organization !== null) {
                return this.loginUIManager.organization.id;
            }
            return dtcOrganizationId;
        };
        await this.smartAuthManager.redirectToClearForOAuth(smartAuthType, window.location.hash, determineOrgId, isTryingAgain);
    }
    handleClearIAL2LoginCallbackIfNeeded() {
        var _a, _b, _c;
        const callbackHash = this.smartAuthManager.getClearIAL2CallbackHash();
        const currentRouteName = (_b = (_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) !== null && _b !== void 0 ? _b : "";
        const isValidRoute = [
            NamedRoutes.PATIENT_CONSENT_ENROLLMENT.routeName,
            NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName,
            NamedRoutes.PATIENT_IAS_CONSENT.routeName,
            NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName,
        ].includes(currentRouteName);
        const isValidStep = ((_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.step) === "login";
        if (callbackHash && isValidRoute && isValidStep) {
            if (this.authManager.isLoggedIn) {
                this.goToNextPage();
            }
            else {
                this.loginUIState = "verifyingWithClearError";
            }
            this.smartAuthManager.resetAuth();
            this.smartAuthManager.setClearIAL2CallbackHash(null);
        }
        else {
            this.smartAuthManager.resetAuth();
        }
    }
    clearPreviewModeTimer() {
        if (this.previewModeTimer) {
            clearTimeout(this.previewModeTimer);
            this.previewModeTimer = null;
        }
    }
}
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentLoginViewModel.prototype, "howItWorksFindRecordsText", null);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentLoginViewModel.prototype, "healthExVerificationProgress", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], PatientConsentLoginViewModel.prototype, "loginUIState", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentLoginViewModel.prototype, "howItWorksTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentLoginViewModel.prototype, "howItWorksSupportingText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentLoginViewModel.prototype, "howItWorksReviewRecordsText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentLoginViewModel.prototype, "isLoggingIn", null);
